import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * Envoltorio de ruta que redirige al inicio de sesión si el usuario no está autenticado.
 * Muestra un mensaje de no autorizado si el usuario está autenticado pero no es administrador.
 * Preserva el destino previsto para la redirección después del inicio de sesión.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation()
  const { user, isLoading, isSignedIn, isUnauthorized, signOut } = useAuth()

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Verificando acceso...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isSignedIn || !user) {
    // Pass the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Show unauthorized message if user is authenticated but not an admin
  if (isUnauthorized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="mx-4 max-w-md rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
          <div className="mb-4 flex justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-destructive">
            Acceso Denegado
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Tu cuenta ({user.email}) no tiene privilegios de administrador.
            Por favor, contacta al administrador del sistema si crees que esto es un error.
          </p>
          <button
            onClick={() => signOut()}
            className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 transition-colors"
          >
            Cerrar Sesión
          </button>
        </div>
      </div>
    )
  }

  // User is authenticated and is an admin - render children
  return <>{children}</>
}
